import { useState } from 'react';
import { toast } from 'react-toastify';
import { usePropertyAPI, UsePropertyAPIReturn } from '../usePropertyAPI';
import {
  usePropertyUIState,
  UsePropertyUIStateReturn,
} from '../usePropertyUIState';
import {
  buildPendingStage,
  mergePendingStage,
  RESET_STAGE_SELECTION,
  updateStageField,
  validatePropertyFields,
} from 'src/components/PropertyForm/hooks/usePropertyFormOrchestrator/helpers';
import {
  usePropertyFormState,
  UsePropertyFormDataReturn,
} from 'src/components/PropertyForm/hooks/usePropertyFormState';

type ButtonAction = 'SaveAndClose' | 'SaveAndAdd';

const DUPLICATE_PROPERTY_MESSAGE =
  'You have already a property with same name and location to that Property Developer.';

interface UsePropertyFormOrchestratorArgs {
  initialData?: any;
  initialModulePermissions?: any[];
  onSaveProperty: (
    data: any,
    action: ButtonAction
  ) => void | Promise<{ ok: boolean }>;
  onClose: () => void;
  onClearValidation?: () => void;
  onModulePermissionsChange?: (perms: any[]) => void;
}

export interface UsePropertyFormOrchestratorReturn
  extends
    UsePropertyAPIReturn,
    UsePropertyUIStateReturn,
    UsePropertyFormDataReturn {
  isSubmitting: boolean;
  validateAndSave: (buttonAction: ButtonAction) => Promise<void>;
  onAddNewConstructionStatus: (type: boolean) => void;
  onhandleStatusUpdate: (value: any, index: number, type: string) => void;
  onhandleStatusDelete: (index: number, obj: any) => void;
  onChangeRank: (data: any, index: number) => void;
  onSetModules: (selectedModule: any) => void;
}

export const usePropertyFormOrchestrator = ({
  initialData,
  initialModulePermissions,
  onSaveProperty,
  onClose,
  onClearValidation,
  onModulePermissionsChange,
}: UsePropertyFormOrchestratorArgs): UsePropertyFormOrchestratorReturn => {
  const api: UsePropertyAPIReturn = usePropertyAPI();
  const { findDuplicateProperty } = api;

  const formState: UsePropertyFormDataReturn = usePropertyFormState(
    initialData,
    initialModulePermissions
  );

  const {
    propertyFormData,
    clearFormData,
    setFormData,
    selectedModulePermissions,
    setSelectedModulePermissions,
  }: UsePropertyFormDataReturn = formState;

  const uiState: UsePropertyUIStateReturn = usePropertyUIState();

  const { setErrors, setDisableButton }: UsePropertyUIStateReturn = uiState;

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validateAndSave = async (buttonAction: ButtonAction): Promise<void> => {
    const newErrors: any = validatePropertyFields(propertyFormData);

    setErrors(newErrors);

    if (newErrors.errors) {
      return;
    }

    const hasDuplicate: boolean = await findDuplicateProperty({
      id: propertyFormData.id,
      name: propertyFormData.name,
      city: propertyFormData.city,
      propertyManagerId: propertyFormData.propertyManagerId,
    });

    if (hasDuplicate) {
      toast.error(`🚫 ${DUPLICATE_PROPERTY_MESSAGE}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      return;
    }

    setDisableButton(true);
    setIsSubmitting(true);

    const saveData: any = {
      ...propertyFormData,
      constructionStages: mergePendingStage(propertyFormData),
    };

    try {
      await onSaveProperty(saveData, buttonAction);

      clearFormData();
      onClearValidation?.();

      if (buttonAction === 'SaveAndClose') {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
      setDisableButton(false);
    }
  };

  const onAddNewConstructionStatus = (hasPendingSelection: boolean): void => {
    const existingStages: any[] = propertyFormData?.constructionStages ?? [];

    if (!hasPendingSelection) {
      const isEditingExistingProperty: boolean = !!propertyFormData?.id;

      const stagesWithAppendedBlankRow: any[] = [
        ...existingStages,
        { new: true },
      ];

      const stagesInitializedWithBlankRow: any[] = [{ new: true }];

      const nextStages: any[] = isEditingExistingProperty
        ? stagesWithAppendedBlankRow
        : stagesInitializedWithBlankRow;

      const blankRowUpdate: any = {
        constructionStages: nextStages,
        ...RESET_STAGE_SELECTION,
      };

      setFormData(blankRowUpdate);

      return;
    }

    const committedStage: any = buildPendingStage(propertyFormData);

    if (existingStages.length === 0) {
      const initialCommitUpdate: any = {
        constructionStages: [committedStage],
        ...RESET_STAGE_SELECTION,
      };

      setFormData(initialCommitUpdate);

      return;
    }

    const stagesWithoutPlaceholder: any[] = existingStages.filter(
      (cs: any) => !cs.new
    );

    const stagesWithCommittedAndBlankRow: any[] = [
      ...stagesWithoutPlaceholder,
      committedStage,
      { new: true },
    ];

    const commitAndContinueUpdate: any = {
      constructionStages: stagesWithCommittedAndBlankRow,
      ...RESET_STAGE_SELECTION,
    };

    setFormData(commitAndContinueUpdate);
  };

  const onhandleStatusUpdate = (
    value: any,
    index: number,
    fieldName: string
  ): void => {
    const updatedStages: any[] = propertyFormData.constructionStages.map(
      (stage: any, stageIndex: number) =>
        stageIndex === index ? updateStageField(stage, fieldName, value) : stage
    );

    setFormData({ constructionStages: updatedStages });
  };

  const onhandleStatusDelete = (_index: number, obj: any): void => {
    let data = propertyFormData.constructionStages.filter(
      (ps: any) => ps !== obj
    );
    setFormData({
      constructionStages: data,
    });
  };

  const onChangeRank = (data: any, _index: number): void => {
    let sortArray = propertyFormData.constructionStages.map((ps: any) => {
      if (ps === uiState.sequence) {
        return data;
      } else if (ps === data) {
        return uiState.sequence;
      } else {
        return ps;
      }
    });

    setFormData({
      constructionStages: sortArray,
    });
  };

  const onSetModules = (selectedModule: any): void => {
    let nextPermissions = selectedModulePermissions;

    let index = nextPermissions.findIndex(
      (object: any) => object.Id === selectedModule
    );

    if (index === -1) {
      const appended = [...selectedModulePermissions, { Id: selectedModule }];
      setSelectedModulePermissions(appended);
      setFormData({ modulePermissions: appended });
      onModulePermissionsChange?.(appended);
    } else {
      nextPermissions = nextPermissions.filter(
        (data: any) => data.Id !== selectedModule
      );

      setSelectedModulePermissions(nextPermissions);
      setFormData({ modulePermissions: nextPermissions });
      onModulePermissionsChange?.(nextPermissions);
    }
  };

  return {
    ...api,
    ...formState,
    ...uiState,
    isSubmitting,
    validateAndSave,
    onAddNewConstructionStatus,
    onhandleStatusUpdate,
    onhandleStatusDelete,
    onChangeRank,
    onSetModules,
  };
};
